// ---------------------------------------------------------------------------
// SPEC-STALL-HEARTBEAT-001: Two-tier stall detection constants (REQ-UBI-002)
// INVARIANT: HEARTBEAT_STALE_THRESHOLD_MS must stay at 3× the backend
// JOB_HEARTBEAT_INTERVAL_SECONDS (30 s). Update both together.
// ---------------------------------------------------------------------------
const HEARTBEAT_STALE_THRESHOLD_MS = 90_000; // 3× backend coordinator interval
const COMPLETED_STALE_THRESHOLD_MS = 120_000; // preserved from old STALL_TIMEOUT_MS
const HEARTBEAT_INITIAL_GRACE_MS = 60_000; // 2× coordinator interval safety margin

/** Return type for evaluateStallState. */
export type StallState = 'none' | 'soft' | 'hard';

/**
 * Evaluate the two-tier stall state for a generation job.
 *
 * Pure function — no side effects, deterministic given the same inputs.
 * Callers in polling loops pass `now` captured at poll-response receipt to
 * limit clock-skew error to one polling interval.
 */
export function evaluateStallState(
  job: {
    status: string;
    completed: number;
    last_heartbeat_at?: string | null;
    started_at?: string | null;
    queue_reason?: string | null;
    server_now?: string | null;
    job_kind?: string | null;
  },
  now: number,
  previousCompleted: number,
  completedStaleMs: number
): StallState {
  // REQ-STA-002: queued jobs never trigger stall detection
  // REQ-STA-003: cancelled jobs suppress all messages
  if (job.status !== 'processing') return 'none';

  // A job held by the deployment gate (or being resumed after a deploy) is
  // paused on purpose; a quiet heartbeat there is expected, not a stall.
  if (job.queue_reason === 'deployment_gate' || job.queue_reason === 'deployment_resume') return 'none';

  // REQ-EVT-001 / REQ-EVT-004: progress advancing clears all stall states
  if (job.completed > previousCompleted) return 'none';

  // Server timestamps are compared against the server clock when the poll
  // carries it, so a skewed client clock cannot fake or hide a stall.
  const serverNow = job.server_now ? Date.parse(job.server_now) : NaN;
  const reference = Number.isFinite(serverNow) ? serverNow : now;

  // REQ-STA-001: initial grace period — suppress ALL warnings when heartbeat
  // has not yet arrived and started_at is within HEARTBEAT_INITIAL_GRACE_MS.
  if (isInInitialGrace(job, reference)) return 'none';

  // Evaluate heartbeat freshness (REQ-EVT-003)
  if (isHeartbeatStale(job, reference)) return 'hard';

  // Heartbeat is fresh; check completed staleness (REQ-EVT-002). A one-click
  // mock exam job is one long AI call (total 1), so a static `completed` is
  // expected there; only the heartbeat-based hard stall applies.
  if (job.job_kind === 'oneclick_mock' || job.job_kind === 'oneclick_ocr') return 'none';
  if (completedStaleMs >= COMPLETED_STALE_THRESHOLD_MS) return 'soft';

  return 'none';
}

/**
 * Return true when the job is still within the initial grace period.
 * Grace applies only when last_heartbeat_at is absent and started_at is recent.
 * REQ-STA-001.
 */
function isInInitialGrace(
  job: { last_heartbeat_at?: string | null; started_at?: string | null },
  now: number
): boolean {
  if (job.last_heartbeat_at) return false;
  if (!job.started_at) return false;
  return now - new Date(job.started_at).getTime() < HEARTBEAT_INITIAL_GRACE_MS;
}

/**
 * Return true when the job's heartbeat signal is stale.
 * Assumes initial grace has already been checked (not in grace period).
 * §8.1 option 1: `now` is the client time captured at poll-response receipt.
 */
function isHeartbeatStale(
  job: { last_heartbeat_at?: string | null; started_at?: string | null },
  now: number
): boolean {
  if (job.last_heartbeat_at) {
    return now - new Date(job.last_heartbeat_at).getTime() >= HEARTBEAT_STALE_THRESHOLD_MS;
  }
  // null heartbeat past grace period — treat as stale (REQ-EVT-003)
  if (job.started_at) {
    return now - new Date(job.started_at).getTime() >= HEARTBEAT_INITIAL_GRACE_MS;
  }
  // No timing info at all — assume stale (conservative)
  return true;
}
